import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import xhr from 'xhr';
import dayjs from 'dayjs';
import AiPagination from 'components/monitoring/ai/AiPagination';
import { BRAND, EmptyState, PageHeader, SectionCard } from './components/ui';

const PAGE_SIZE = 20;

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

const inFilter = (field, ids) => ids.map((id, index) => `filters${field}[$in][${index}]=${id}`).join('&');

// Convenio de cada estudiante de la página. Un estudiante "es de convenio" si se
// registró con el link de un afiliado (affiliate-record) que tiene un convenio
// (agreement, cuyo dueño es el usuario del afiliado). Devuelve
// { [studentId]: { affiliate: 'Nombre', agreements: ['Convenio'] } }.
const loadAgreementInfo = async (students) => {
  const studentIds = students.map((el) => el.id);
  if (!studentIds.length) return {};

  const resRecords = await xhr(
    `/affiliate-records?${inFilter('[student][id]', studentIds)}&populate[student]=true&populate[affiliate][populate]=user&pagination[pageSize]=100`
  );
  const byStudent = {};
  resRecords.data.data.forEach((record) => {
    const studentId = record.student?.id;
    if (studentId && record.affiliate) byStudent[studentId] = { affiliateUser: record.affiliate.user };
  });

  const userIds = [...new Set(Object.values(byStudent).map((el) => el.affiliateUser?.id).filter(Boolean))];
  let agreementsByUser = {};
  if (userIds.length) {
    try {
      const resAgreements = await xhr(
        `/agreements?${inFilter('[User][id]', userIds)}&populate[User]=true&pagination[pageSize]=100`
      );
      resAgreements.data.data.forEach((agreement) => {
        const userId = agreement.User?.id;
        if (!userId) return;
        agreementsByUser[userId] = [...(agreementsByUser[userId] || []), agreement.nombre || agreement.razon_social];
      });
    } catch (error) {
      // Sin permiso de lectura en "agreement" se muestra solo el afiliado.
      console.error(error);
      agreementsByUser = {};
    }
  }

  return Object.fromEntries(
    Object.entries(byStudent).map(([studentId, { affiliateUser }]) => [
      studentId,
      { affiliate: fullName(affiliateUser) || 'Sin nombre', agreements: agreementsByUser[affiliateUser?.id] || [] },
    ])
  );
};

function AgreementCell({ info }) {
  if (!info) return <Text color="gray.400">—</Text>;
  if (info.agreements.length) {
    return (
      <Text fontWeight="600" color="gray.800" whiteSpace="normal">
        {info.agreements.join(', ')}
      </Text>
    );
  }
  return (
    <Text fontSize="sm" color="gray.500" whiteSpace="normal">
      Afiliado: {info.affiliate}
    </Text>
  );
}

const UsersAdviser = () => {
  const [students, setStudents] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [agreementInfo, setAgreementInfo] = React.useState({});

  const getStudents = async (page) => {
    try {
      setLoading(true);
      const res = await xhr(
        `/students?pagination[page]=${page}&pagination[pageSize]=${PAGE_SIZE}&sort[0]=createdAt:DESC&populate[user]=true&populate[adviser][populate]=user`
      );
      setTotal(res.data.meta.pagination.total);
      setStudents(res.data.data);
      try {
        setAgreementInfo(await loadAgreementInfo(res.data.data));
      } catch (error) {
        console.error(error);
        setAgreementInfo({});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getStudents(currentPage);
  }, [currentPage]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <PageHeader title="Estudiantes registrados" subtitle={`${total.toLocaleString('es-CO')} estudiantes, del más reciente al más antiguo.`} />

      <SectionCard padding={0} overflow="hidden">
        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner color={BRAND} />
          </Flex>
        ) : students.length === 0 ? (
          <EmptyState title="No hay estudiantes registrados" />
        ) : (
          <TableContainer>
            <Table>
              <Thead bg="gray.50">
                <Tr>
                  <Th>Estudiante</Th>
                  <Th>Convenio</Th>
                  <Th>Asignado a</Th>
                  <Th>Celular</Th>
                  <Th>Fecha</Th>
                  <Th textAlign="right">Detalle</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((el) => (
                  <Tr key={el.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {fullName(el.user) || 'Sin nombre'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Código {el.id}
                      </Text>
                    </Td>
                    <Td>
                      <AgreementCell info={agreementInfo[el.id]} />
                    </Td>
                    <Td fontSize="sm">{fullName(el.adviser?.user) || '—'}</Td>
                    <Td fontSize="sm">{el.user?.cellphone}</Td>
                    <Td fontSize="sm">{dayjs(el.createdAt).format('DD/MM/YYYY')}</Td>
                    <Td textAlign="right">
                      <Button
                        as={Link}
                        to={`/plataforma/asesoras/ver-estudiante/${el.id}`}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                      >
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {!loading && (
          <AiPagination
            pagination={{ page: currentPage, pageSize: PAGE_SIZE, total, pageCount }}
            noun="estudiantes"
            onPageChange={setCurrentPage}
          />
        )}
      </SectionCard>
    </>
  );
};

export default UsersAdviser;
